import { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { ISanityBlockContent } from '@rbi-ctg/menu';
import {
  useAllFeatureIdsQuery,
  useFeatureAccountUpdatedAgreementsQuery,
} from 'generated/sanity-graphql';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { brand } from 'utils/environment';

export interface IUseFeatureAccountUpdatedAgreements {
  featureAccountUpdatedAgreementsLoading: boolean;
  marketingCommunicationText: string;
  updateAgreementText: string;
}

export interface ISanityAccountUpdatedAgreements {
  locale?: [ISanityBlockContent];
  __typename?: string;
}

export type SanityObject = ISanityAccountUpdatedAgreements | null | undefined;

export const useFeatureAccountUpdatedAgreements = (): IUseFeatureAccountUpdatedAgreements => {
  const enableAcceptanceAgreementsFromSanity = useFlag(
    LaunchDarklyFlag.ENABLE_ACCEPTANCE_AGREEMENTS_DESCRIPTION_FROM_SANITY
  );

  const { formatMessage } = useIntl();

  const { data: dataAllFeatureIds, loading: featureIdsLoading } = useAllFeatureIdsQuery();

  const featureAccountUpdatedAgreementsId = useMemo(
    () => dataAllFeatureIds?.allFeatureAccountUpdatedAgreements[0]?._id || '',
    [dataAllFeatureIds]
  );

  const { data, loading } = useFeatureAccountUpdatedAgreementsQuery({
    variables: { featureAccountUpdatedAgreementsId },
    skip: !featureAccountUpdatedAgreementsId,
  });

  const isLoading = useMemo(() => featureIdsLoading || loading, [featureIdsLoading, loading]);

  const getText = useCallback(
    (sanityObject: SanityObject, type: string) => {
      const lokaliseUpdatedAgreementsText = formatMessage({ id: type });
      const sanityText =
        sanityObject?.locale?.[0]?.children?.[0]?.text ?? lokaliseUpdatedAgreementsText;

      return enableAcceptanceAgreementsFromSanity ? sanityText : lokaliseUpdatedAgreementsText;
    },
    [enableAcceptanceAgreementsFromSanity, formatMessage]
  );

  return {
    featureAccountUpdatedAgreementsLoading: isLoading,
    marketingCommunicationText: getText(
      data?.FeatureAccountUpdatedAgreements?.marketingCommunicationText,
      `acceptEmailsAndSpecialOffers.${brand()}`
    ),
    updateAgreementText: getText(
      data?.FeatureAccountUpdatedAgreements?.updateAgreementText,
      'updatedAgreementsText'
    ),
  };
};
